import { buildDownloadName, buildZipName } from "./downloadNames.js";

// The file manager (จัดการไฟล์): request parameters, sort choices and the
// names its downloads get. Kept free of Vue so it can be tested directly.

export const PAGE_SIZE = 50;

// Each tab's sort choices, as "column:order". The server accepts only
// creationDt, fileSize and deletedAt.
export const SORT_OPTIONS = {
  active: [
    { value: "creationDt:desc", text: "อัปโหลดล่าสุดก่อน" },
    { value: "creationDt:asc", text: "อัปโหลดเก่าสุดก่อน" },
    { value: "fileSize:desc", text: "ไฟล์ใหญ่สุดก่อน" },
    { value: "fileSize:asc", text: "ไฟล์เล็กสุดก่อน" },
  ],
  trash: [
    { value: "deletedAt:desc", text: "ลบล่าสุดก่อน" },
    { value: "deletedAt:asc", text: "ลบเก่าสุดก่อน" },
    { value: "fileSize:desc", text: "ไฟล์ใหญ่สุดก่อน" },
  ],
};

export const defaultSort = tab => SORT_OPTIONS[tab][0].value;

/**
 * Query parameters for GET /api/mediaFile/manage.
 *
 * @param {Object} state
 * @param {"active"|"trash"} state.tab
 * @param {string} [state.category] - "all" or a media category
 * @param {string} [state.q]
 * @param {string} [state.sort] - one of SORT_OPTIONS' values
 * @param {number} [state.offset]
 * @param {number} [state.limit]
 */
export function buildManageParams({
  tab,
  category,
  q,
  sort,
  offset = 0,
  limit = PAGE_SIZE,
}) {
  const allowed = SORT_OPTIONS[tab] || SORT_OPTIONS.active;
  const chosen = allowed.some(o => o.value === sort) ? sort : allowed[0].value;
  const [column, order] = chosen.split(":");
  const params = { sort: column, order, offset, limit };
  if (tab === "trash") params.softDeleted = true;
  if (category && category !== "all") params.category = category;
  const words = String(q || "").trim().replace(/\s+/g, " ");
  if (words) params.q = words.slice(0, 100);
  return params;
}

/** The patient's name as staff read it: Thai first, English otherwise. */
export function patientName(patient) {
  if (!patient) return "";
  const th = [patient.firstNameTh, patient.lastNameTh].filter(Boolean).join(" ");
  if (th) return th;
  return [patient.firstNameEn, patient.lastNameEn].filter(Boolean).join(" ");
}

// Clinic forms belong to no patient, so they carry no DN.
const dnOf = item =>
  item.category === "Template" ? null : (item.patient && item.patient.dn) || null;

/**
 * 20260926_XRay_DN0145_PA-#46.jpg, dated by the treatment when there is one.
 *
 * @param {Object} item - a formatted media file with `patient` and `treatmentDt`
 */
export function manageDownloadName(item) {
  return buildDownloadName(item, {
    dn: dnOf(item),
    treatmentDate: item.treatmentDt || null,
  });
}

/** A zip of files from one patient carries that DN; mixed files do not. */
export function manageZipName(files) {
  const dns = new Set(files.map(dnOf));
  const dn = dns.size === 1 ? [...dns][0] : null;
  return buildZipName({ dn, label: "ไฟล์" });
}

/**
 * Takes files out of the loaded list and the counts after they were moved
 * to the trash, restored or deleted.
 *
 * @returns {{ items: Object[], total: number, categoryCounts: Object<string, number> }}
 */
export function withoutFiles({ items, total, categoryCounts }, removed) {
  const gone = new Set(removed.map(f => f.id));
  const counts = { ...categoryCounts };
  let dropped = 0;
  items.forEach(item => {
    if (!gone.has(item.id)) return;
    dropped += 1;
    if (counts[item.category]) counts[item.category] -= 1;
  });
  return {
    items: items.filter(item => !gone.has(item.id)),
    total: Math.max(0, total - dropped),
    categoryCounts: counts,
  };
}
